import type { Platform } from '../lib/platform';

/**
 * Shortcut strings: modifiers joined with "+", key last, lower-case.
 *   'mod+k' (⌘ on Apple, Ctrl elsewhere), 'shift+arrowup', 'alt+arrowleft', '?', '+', 'mod++', 'delete'.
 * Modifiers: mod, ctrl, meta (cmd), alt (option), shift.
 */
export interface KeyChord {
  key: string;
  mod: boolean;
  ctrl: boolean;
  meta: boolean;
  alt: boolean;
  shift: boolean;
}

const MODIFIERS = new Set(['mod', 'ctrl', 'meta', 'cmd', 'alt', 'option', 'shift']);

const KEY_ALIASES: Record<string, string> = {
  ' ': 'space',
  spacebar: 'space',
  esc: 'escape',
  del: 'delete',
  return: 'enter',
  up: 'arrowup',
  down: 'arrowdown',
  left: 'arrowleft',
  right: 'arrowright',
  plus: '+',
};

export function normalizeKey(key: string): string {
  const k = key.toLowerCase();
  return KEY_ALIASES[k] ?? k;
}

export function parseShortcut(shortcut: string): KeyChord {
  const s = shortcut.trim().toLowerCase();
  let parts: string[];
  if (s === '+') parts = ['+'];
  else if (s.endsWith('++')) parts = [...s.slice(0, -2).split('+').filter(Boolean), '+'];
  else parts = s.split('+');
  const chord: KeyChord = {
    key: '',
    mod: false,
    ctrl: false,
    meta: false,
    alt: false,
    shift: false,
  };
  for (const p of parts) {
    if (MODIFIERS.has(p) && p !== parts[parts.length - 1]) {
      if (p === 'mod') chord.mod = true;
      else if (p === 'ctrl') chord.ctrl = true;
      else if (p === 'meta' || p === 'cmd') chord.meta = true;
      else if (p === 'alt' || p === 'option') chord.alt = true;
      else chord.shift = true;
    } else {
      chord.key = normalizeKey(p);
    }
  }
  if (chord.key === '') throw new Error(`Invalid shortcut "${shortcut}"`);
  return chord;
}

export interface KeyEventLike {
  key: string;
  code?: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}

/**
 * Logical key of an event. With Alt (Option on macOS) the produced character changes
 * (⌥A = "å"), so letters/digits fall back to the physical code.
 */
export function eventKey(e: KeyEventLike): string {
  if (e.altKey && e.code) {
    const letter = /^Key([A-Z])$/.exec(e.code);
    if (letter) return letter[1]!.toLowerCase();
    const digit = /^Digit(\d)$/.exec(e.code);
    if (digit) return digit[1]!;
  }
  return normalizeKey(e.key);
}

/** Printable symbols whose Shift state depends on the keyboard layout ("?", "+", …). */
function isLayoutSymbol(key: string): boolean {
  return key.length === 1 && !/[a-z0-9]/.test(key);
}

export function matchesChord(chord: KeyChord, e: KeyEventLike, platform: Platform): boolean {
  if (eventKey(e) !== chord.key) return false;
  const wantMeta = chord.meta || (platform === 'mac' && chord.mod);
  const wantCtrl = chord.ctrl || (platform !== 'mac' && chord.mod);
  if (e.metaKey !== wantMeta || e.ctrlKey !== wantCtrl || e.altKey !== chord.alt) return false;
  if (!isLayoutSymbol(chord.key) && e.shiftKey !== chord.shift) return false;
  return true;
}

export function matchesShortcut(shortcut: string, e: KeyEventLike, platform: Platform): boolean {
  return matchesChord(parseShortcut(shortcut), e, platform);
}

const MAC_SYMBOLS: Record<string, string> = {
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
  enter: '↩',
  escape: 'Esc',
  delete: '⌦',
  backspace: '⌫',
  tab: '⇥',
  space: 'Space',
};

const OTHER_NAMES: Record<string, string> = {
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
  enter: 'Enter',
  escape: 'Esc',
  delete: 'Del',
  backspace: 'Backspace',
  tab: 'Tab',
  space: 'Space',
};

function keyLabel(key: string, platform: Platform): string {
  const map = platform === 'mac' ? MAC_SYMBOLS : OTHER_NAMES;
  if (map[key]) return map[key];
  if (/^f\d{1,2}$/.test(key)) return key.toUpperCase();
  return key.length === 1 ? key.toUpperCase() : key[0]!.toUpperCase() + key.slice(1);
}

/** Human readable shortcut: '⌘⇧Z' on macOS, 'Ctrl+Shift+Z' elsewhere. */
export function formatShortcut(shortcut: string, platform: Platform): string {
  const c = parseShortcut(shortcut);
  if (platform === 'mac') {
    return `${c.ctrl ? '⌃' : ''}${c.alt ? '⌥' : ''}${c.shift ? '⇧' : ''}${c.mod || c.meta ? '⌘' : ''}${keyLabel(c.key, platform)}`;
  }
  const parts: string[] = [];
  if (c.mod || c.ctrl) parts.push('Ctrl');
  if (c.meta) parts.push('Win');
  if (c.alt) parts.push('Alt');
  if (c.shift) parts.push('Shift');
  parts.push(keyLabel(c.key, platform));
  return parts.join('+');
}

/** Same chord on the given platform (used for conflict detection). */
export function chordId(shortcut: string, platform: Platform): string {
  const c = parseShortcut(shortcut);
  const meta = c.meta || (platform === 'mac' && c.mod);
  const ctrl = c.ctrl || (platform !== 'mac' && c.mod);
  const shift = isLayoutSymbol(c.key) ? false : c.shift;
  return `${ctrl ? 'C' : ''}${meta ? 'M' : ''}${c.alt ? 'A' : ''}${shift ? 'S' : ''}:${c.key}`;
}

export interface TargetLike {
  tagName?: string;
  isContentEditable?: boolean;
  type?: string;
  getAttribute?(name: string): string | null;
}

const NON_TEXT_INPUTS = new Set([
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'range',
  'color',
  'file',
]);

/** True when keystrokes belong to a text field (shortcuts must not steal them). */
export function isTypingTarget(target: TargetLike | null | undefined): boolean {
  if (!target || typeof target.tagName !== 'string') return false;
  const tag = target.tagName.toUpperCase();
  if (target.isContentEditable) return true;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') return !NON_TEXT_INPUTS.has((target.type ?? 'text').toLowerCase());
  return target.getAttribute?.('role') === 'textbox';
}
